import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

export function useStudentSkills(userId?: string) {
  const { user } = useAuth();
  const targetId = userId ?? user?.id;
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["student_skills", targetId],
    enabled: !!targetId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("student_skills")
        .select("skill_id")
        .eq("user_id", targetId!);
      if (error) throw error;
      return (data ?? []).map((d) => d.skill_id);
    },
  });

  const setSkills = useMutation({
    mutationFn: async (skillIds: string[]) => {
      if (!user) throw new Error("Not authenticated");
      const uid = user.id;

      // Delete existing then insert new — check both operations
      const { error: delError } = await supabase.from("student_skills").delete().eq("user_id", uid);
      if (delError) throw delError;

      if (skillIds.length > 0) {
        const rows = skillIds.map(skill_id => ({ user_id: uid, skill_id }));
        const { error } = await supabase.from("student_skills").insert(rows);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["student_skills", user?.id] });
    },
  });

  return {
    skills: query.data ?? [],
    isLoading: query.isLoading,
    setSkills,
  };
}
